import { useQueries, useQuery } from '@tanstack/react-query'
import { createContext, type ReactNode } from 'react'

import { fetchAreas } from '@/features/areas/api'

import { fetchDcs } from '../../dcs/api/fetch-dcs'
import {
  fetchSites,
  fetchSitesWithArea,
  fetchTruckClasses,
  fetchTruckTypes
} from '../api'
import { type ConfigEntities } from '../models'
import { configQueryKeys } from '../query-keys'

export type ConfigContextType = ConfigEntities | null

export const ConfigContext = createContext<ConfigContextType>(null)

export function ConfigProvider({
  tenantKey,
  dcId,
  children
}: {
  tenantKey: string
  dcId: number
  children: ReactNode
}) {
  const [truckClassesQuery, truckTypesQuery, dcsQuery] = useQueries({
    queries: [
      {
        queryKey: configQueryKeys.truckClasses(),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchTruckClasses(tenantKey, signal),
        retry: false
      },
      {
        queryKey: configQueryKeys.truckTypes(),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchTruckTypes(tenantKey, signal),
        retry: false
      },
      {
        queryKey: configQueryKeys.dcs(),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const res = await fetchDcs({ tenantKey }, signal)
          return res.data
        },
        retry: false
      }
    ]
  })

  if (
    truckClassesQuery.isError ||
    truckTypesQuery.isError ||
    dcsQuery.isError
  ) {
    throw truckClassesQuery.error ?? truckTypesQuery.error ?? dcsQuery.error
  }

  const truckClasses = truckClassesQuery.data
  const truckTypes = truckTypesQuery.data
  const dcs = dcsQuery.data

  // 地点関連はデータ量が多いので、取得タイミングをずらす
  // 1: 車格、車両属性、拠点の取得を待つ
  // 2: 地点、エリアの取得を開始
  // 3: 地点＋エリア情報の取得を開始

  const isPrereqQueriesReady =
    truckClassesQuery.isPending &&
    truckTypesQuery.isPending &&
    dcsQuery.isPending

  const sitesQuery = useQuery({
    queryKey: configQueryKeys.sites(dcId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchSites({ tenantKey, dcId }, signal),
    retry: false,
    enabled: isPrereqQueriesReady
  })

  const areasQuery = useQuery({
    queryKey: configQueryKeys.areas(),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchAreas({ tenantKey }, signal),
    retry: false,
    enabled: isPrereqQueriesReady
  })

  if (sitesQuery.isError || areasQuery.isError) {
    throw sitesQuery.error ?? areasQuery.error
  }

  const sitesWithAreaQuery = useQuery({
    queryKey: configQueryKeys.sitesWithArea(dcId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchSitesWithArea({ tenantKey, dcId }, signal),
    retry: false,
    enabled: sitesQuery.isPending && areasQuery.isPending
  })

  if (sitesWithAreaQuery.isError) {
    throw sitesWithAreaQuery.error
  }

  return (
    <ConfigContext.Provider
      value={{
        truckClasses: truckClasses ?? [],
        truckTypes: truckTypes ?? [],
        dcs: dcs ?? [],
        sites: sitesQuery.data ?? [],
        areas: areasQuery.data ?? [],
        sitesWithArea: sitesWithAreaQuery.data ?? []
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
